import { useState, type ReactElement } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { createAppQueryClient } from './entitlements/denial'
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom'
import { SessionProvider, useSession } from './context/SessionContext'
import { safeNext } from './context/pendingInvite'
import { Shell } from './components/Shell'
import { LoginView } from './views/LoginView'
import { SignupView } from './views/SignupView'
import { ForgotPasswordView } from './views/ForgotPasswordView'
import { ResetPasswordView } from './views/ResetPasswordView'
import { InviteView } from './views/InviteView'

// Signed-out only: a signed-in visitor goes to `?next=` (e.g. back to an invite) or home.
function SignedOutOnly({ children }: { children: ReactElement }) {
  const { status } = useSession()
  const [params] = useSearchParams()
  if (status !== 'authenticated') return children
  return <Navigate to={safeNext(params.get('next')) ?? '/'} replace />
}

// AppGate is the only place that decides between login and the shell.
function AppGate() {
  const { status, recheck } = useSession()

  if (status === 'loading') {
    return (
      <div className="app-splash" role="status" aria-live="polite" aria-label="Loading">
        <span>Loading…</span>
      </div>
    )
  }
  if (status === 'error') {
    return (
      <div className="app-splash" role="alert">
        <p>Can’t reach the Symone gateway.</p>
        <button type="button" className="btn" onClick={recheck}>
          Retry
        </button>
      </div>
    )
  }
  // Public account screens render outside the session gate. Reset and invite also
  // render when signed in (accepting an invite needs the session).
  return (
    <Routes>
      <Route path="/login" element={<SignedOutOnly><LoginView /></SignedOutOnly>} />
      <Route path="/signup" element={<SignedOutOnly><SignupView /></SignedOutOnly>} />
      <Route path="/forgot-password" element={<SignedOutOnly><ForgotPasswordView /></SignedOutOnly>} />
      <Route path="/reset-password" element={<ResetPasswordView />} />
      <Route path="/invite/:token?" element={<InviteView />} />
      <Route path="*" element={status === 'unauthenticated' ? <LoginView /> : <Shell />} />
    </Routes>
  )
}

export default function App() {
  const [queryClient] = useState(
    () =>
      // Any plan / credits denial re-reads GET /entitlements once (simple-mode S4 4.2).
      createAppQueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 30_000 },
          mutations: { retry: 0 },
        },
      }),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <AppGate />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
